import { saveAs } from 'file-saver';
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { injectMonthlyPulseVariables } from './injectMonthlyPulseVariables';
import { IMonthlyPulseVariables } from './MonthlyPulseVariables';

const DASHBOARD_URL = '/members/components/monthly-pulse/render/dashboard.html';
const DESIGN_WIDTH = 1920;
const PRINT_IMAGES_TIMEOUT_MS = 5000;
const PRINT_FRAME_CLEANUP_TIMEOUT_MS = 60000;

interface IProps {
  variables: IMonthlyPulseVariables | null;
}

const MonthlyPulseDashboard: React.FC<IProps> = ({ variables }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [dashboardHtml, setDashboardHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(1080);

  const updateScale = () => {
    const width = wrapperRef.current?.clientWidth;
    if (width) {
      setScale(width / DESIGN_WIDTH);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetch(DASHBOARD_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load dashboard (${response.status})`);
        }
        return response.text();
      })
      .then((html) => {
        if (!cancelled) { setDashboardHtml(html); }
      })
      .catch((e: any) => {
        if (!cancelled) { setError(e.message || String(e)); }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  useEffect(() => {
    if (dashboardHtml) { updateScale(); }
  }, [dashboardHtml]);

  const srcDoc = useMemo(() => {
    if (!dashboardHtml || !variables) {
      return undefined;
    }
    const doc = new DOMParser().parseFromString(dashboardHtml, 'text/html');
    injectMonthlyPulseVariables(doc, variables);
    // The print dialog uses the document title as the default pdf filename
    doc.title = `${variables.heading} - ${variables.selectedMonth}`;
    return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
  }, [dashboardHtml, variables]);

  const handleExportPdf = () => {
    if (!srcDoc) { return; }
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.onload = async () => {
      const printWindow = printFrame.contentWindow;
      const printDocument = printFrame.contentDocument;
      if (!printWindow || !printDocument) {
        printFrame.remove();
        return;
      }
      // Wait for images so media cards don't print blank, but never block printing
      const pendingImages = Array.from(printDocument.images)
        .filter((image) => !image.complete)
        .map((image) => new Promise<void>((resolve) => {
          image.onload = () => resolve();
          image.onerror = () => resolve();
        }));
      const timeout = new Promise<void>((resolve) => {
        setTimeout(resolve, PRINT_IMAGES_TIMEOUT_MS);
      });
      await Promise.race([Promise.all(pendingImages), timeout]);
      printWindow.onafterprint = () => printFrame.remove();
      printWindow.focus();
      printWindow.print();
      // Fallback cleanup for browsers that don't fire afterprint
      setTimeout(() => printFrame.remove(), PRINT_FRAME_CLEANUP_TIMEOUT_MS);
    };
    printFrame.srcdoc = srcDoc;
    document.body.appendChild(printFrame);
  };

  const handleExportJson = () => {
    if (!variables) { return; }

    const blob = new Blob([JSON.stringify(variables, null, 2)], { type: 'application/json' });
    saveAs(blob, `${variables.heading} - ${variables.selectedMonth}.json`);
  };

  const handleIframeLoad = () => {
    updateScale();
    const height = iframeRef.current?.contentDocument?.documentElement?.scrollHeight;
    if (height) {
      setContentHeight(height);
    }
  };

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }
  if (!dashboardHtml) {
    return <div>Loading...</div>;
  }
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
        <button
          type='button'
          onClick={handleExportJson}
          disabled={!variables}
          className='jr-btn-default'
          style={{ marginRight: '8px' }}
        >
          Export JSON
        </button>
        <button type='button' onClick={handleExportPdf} disabled={!srcDoc} className='jr-btn-default'>
          Export PDF
        </button>
      </div>
      <div
        ref={wrapperRef}
        style={{
          width: '100%',
          height: contentHeight * scale,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <iframe
          ref={iframeRef}
          title='Monthly Pulse Dashboard'
          srcDoc={srcDoc}
          onLoad={handleIframeLoad}
          style={{
            width: DESIGN_WIDTH,
            height: contentHeight,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        />
      </div>
    </>
  );
};

export default MonthlyPulseDashboard;
